import { useCallback, useEffect, useRef } from 'react';

/**
 * The header search boxes: `search$.pipe(debounceTime(300), distinctUntilChanged())`. Each input pushes its trimmed
 * text; after 300 ms of quiet the last text is emitted unless it equals the previously EMITTED one (the very first
 * emission always passes, even an empty string). Pending text is dropped on unmount, like the unsubscribe.
 */
export function useDebouncedSearch(emit: (searchText: string) => void, delayMs = 300): (text: string) => void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const last = useRef<string | undefined>(undefined);
  const emitRef = useRef(emit);

  useEffect(() => {
    emitRef.current = emit;
  });

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(
    (text: string) => {
      const value = text.trim();
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        if (last.current !== value) {
          last.current = value;
          emitRef.current(value);
        }
      }, delayMs);
    },
    [delayMs],
  );
}
